import { create } from 'zustand';
import { api, setCsrfToken } from '../api/client.js';
import { useI18n, type Locale } from '../i18n/index.js';

export interface SessionUser {
  id: string;
  besbelloSub: string | null;
  email: string;
  name: string;
  roles: string[];
  locale: Locale;
  loyaltyCode: string | null;
  mfaEnabled: boolean;
}

interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  /** Si este despliegue tiene «Entrar con Besbello». Lo dice el servidor. */
  ssoEnabled: boolean;
  load(): Promise<void>;
  login(next?: string): Promise<void>;
  /** Sesion de invitado: se puede disenar, no se puede pagar. */
  entrarComoInvitado(): Promise<void>;
  logout(): Promise<void>;
  isStaff(): boolean;
  isAdmin(): boolean;
}

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  loading: true,
  // Se asume que NO hay SSO hasta que el servidor diga lo contrario: es mejor
  // ofrecer el acceso propio de mas que un boton que va a fallar.
  ssoEnabled: false,

  async load() {
    try {
      const me = await api.me();
      setCsrfToken(me.csrfToken);
      set({
        user: (me.user as SessionUser | null) ?? null,
        ssoEnabled: me.ssoEnabled === true,
        loading: false,
      });

      // El idioma del cliente llega por SSO: la interfaz arranca en SU idioma,
      // no en el del navegador de quien lo abrio.
      if (me.user?.locale) useI18n.getState().setLocale(me.user.locale as Locale);
    } catch {
      set({ user: null, loading: false });
    }
  },

  async login(next?: string) {
    const destino = next ?? window.location.pathname;
    // Sin SSO en este despliegue se va directo al acceso propio, sin intentar
    // una peticion que ya se sabe que va a fallar.
    if (!get().ssoEnabled) {
      window.location.href = `/entrar?next=${encodeURIComponent(destino)}`;
      return;
    }
    const { authorizeUrl } = await api.ssoStart(destino);
    window.location.href = authorizeUrl;
  },

  /*
    Empezar sin cuenta.

    Se recarga la sesion al terminar en vez de fabricar el usuario aqui: el
    servidor es quien sabe que roles trae, y duplicar esa logica en el cliente
    seria la forma de que un dia dejaran de coincidir.
  */
  async entrarComoInvitado() {
    await api.entrarComoInvitado();
    await get().load();
  },

  async logout() {
    await api.logout();
    setCsrfToken(null);
    set({ user: null });
  },

  isStaff() {
    const roles = get().user?.roles ?? [];
    return roles.some((r) => r !== 'client');
  },

  isAdmin() {
    const roles = get().user?.roles ?? [];
    return roles.includes('admin') || roles.includes('catalog_admin');
  },
}));
